import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import { Bell, Save, Clock, Package, Mail, ToggleLeft, ToggleRight } from 'lucide-react';

const AlertsConfigPage = () => {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [config, setConfig] = useState({
    repair_alert_days: 5,
    stock_alert_threshold: 2,
    notify_client_on_status_change: true,
    alert_email_enabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    api.get('/company/alerts-config')
      .then(r => setConfig(c => ({ ...c, ...r.data })))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(null);
    try {
      await api.post('/company/alerts-config', config);
      setMsg({ type: 'success', text: t('alertsConfig.saved') });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || t('alertsConfig.error') });
    }
    setSaving(false);
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#009b3a] border-t-transparent rounded-full animate-spin" /></div></Layout>;

  const Toggle = ({ label, description, checked, onChange, testId }) => (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="text-sm font-medium text-white">{label}</p>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
      <button type="button" onClick={() => onChange(!checked)} data-testid={testId}
        className="transition-all">
        {checked
          ? <ToggleRight className="w-8 h-8 text-[#009b3a]" />
          : <ToggleLeft className="w-8 h-8 text-gray-600" />
        }
      </button>
    </div>
  );

  return (
    <Layout>
    <div className="space-y-6" data-testid="alerts-config-page">
      <div>
        <h1 className="text-xl font-bold text-white flex items-center gap-2"><Bell className="w-5 h-5 text-[#fedd00]" /> {t('alertsConfig.title')}</h1>
        <p className="text-sm text-gray-500 mt-1">{t('alertsConfig.subtitle')}</p>
      </div>

      {msg && (
        <div className={`px-4 py-3 rounded-lg text-xs font-medium text-center ${msg.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`} data-testid="alerts-config-msg">
          {msg.text}
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-4">
        {/* Master toggle */}
        <div className="bg-[#111827] border border-white/5 rounded-xl p-5">
          <Toggle
            label={t('alertsConfig.emailAlertsLabel')}
            description={t('alertsConfig.emailAlertsDesc')}
            checked={config.alert_email_enabled}
            onChange={v => setConfig({ ...config, alert_email_enabled: v })}
            testId="toggle-alerts-enabled"
          />
        </div>

        {/* Repair alerts */}
        <div className={`bg-[#111827] border border-white/5 rounded-xl p-5 space-y-4 transition-opacity ${!config.alert_email_enabled ? 'opacity-40 pointer-events-none' : ''}`}>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-400" />
            <h2 className="text-sm font-semibold text-white">{t('alertsConfig.repairAlertsTitle')}</h2>
          </div>
          <p className="text-xs text-gray-500">
            {t('alertsConfig.repairAlertsDesc')}
          </p>
          <div>
            <label className="block text-[11px] uppercase tracking-wider mb-1.5 font-medium text-gray-500">{t('alertsConfig.delayLabel')}</label>
            <div className="flex items-center gap-3">
              {[3, 5, 7, 10, 14].map(d => (
                <button key={d} type="button"
                  onClick={() => setConfig({ ...config, repair_alert_days: d })}
                  data-testid={`alert-days-${d}`}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${config.repair_alert_days === d
                    ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                    : 'bg-white/[0.02] text-gray-500 border border-white/5 hover:border-white/10'}`}
                >{t('alertsConfig.daysShort', { n: d })}</button>
              ))}
              <input
                type="number"
                min="1"
                max="30"
                value={config.repair_alert_days}
                onChange={e => setConfig({ ...config, repair_alert_days: parseInt(e.target.value) || 5 })}
                data-testid="alert-days-input"
                className="w-20 px-3 py-2 bg-white/[0.03] border border-white/8 rounded-lg text-sm text-white focus:outline-none focus:border-amber-500/40"
              />
            </div>
          </div>
        </div>

        {/* Stock alerts */}
        <div className={`bg-[#111827] border border-white/5 rounded-xl p-5 space-y-4 transition-opacity ${!config.alert_email_enabled ? 'opacity-40 pointer-events-none' : ''}`}>
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-red-400" />
            <h2 className="text-sm font-semibold text-white">{t('alertsConfig.stockAlertsTitle')}</h2>
          </div>
          <p className="text-xs text-gray-500">
            {t('alertsConfig.stockAlertsDesc')}
          </p>
          <div>
            <label className="block text-[11px] uppercase tracking-wider mb-1.5 font-medium text-gray-500">{t('alertsConfig.lowStockThresholdLabel')}</label>
            <div className="flex items-center gap-3">
              {[1, 2, 3, 5, 10].map(t => (
                <button key={t} type="button"
                  onClick={() => setConfig({ ...config, stock_alert_threshold: t })}
                  data-testid={`stock-threshold-${t}`}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${config.stock_alert_threshold === t
                    ? 'bg-red-500/15 text-red-400 border border-red-500/30'
                    : 'bg-white/[0.02] text-gray-500 border border-white/5 hover:border-white/10'}`}
                >{t}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Client notifications */}
        <div className="bg-[#111827] border border-white/5 rounded-xl p-5">
          <Toggle
            label={t('alertsConfig.clientNotifLabel')}
            description={t('alertsConfig.clientNotifDesc')}
            checked={config.notify_client_on_status_change}
            onChange={v => setConfig({ ...config, notify_client_on_status_change: v })}
            testId="toggle-client-notify"
          />
          <div className="mt-3 px-3 py-2 bg-[#009b3a]/5 border border-[#009b3a]/10 rounded-lg">
            <p className="text-[11px] text-[#009b3a]/80 flex items-center gap-1.5">
              <Mail className="w-3 h-3" />
              {t('alertsConfig.emailSenderInfo')}
            </p>
          </div>
        </div>

        <button type="submit" disabled={saving} data-testid="alerts-save-btn"
          className="px-5 py-2.5 rounded-lg text-sm font-semibold text-[#050a14] transition-all disabled:opacity-50 flex items-center gap-2"
          style={{ background: '#009b3a' }}>
          <Save className="w-4 h-4" /> {saving ? t('alertsConfig.saving') : t('alertsConfig.save')}
        </button>
      </form>
    </div>
    </Layout>
  );
};

export default AlertsConfigPage;
